/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/jsx-sort-props */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { observer } from 'mobx-react-lite';

import CampaignView from '../components/Campaign/CampaignView';
import { CommandController } from '../components/Campaign/CommandController';
import { ImageController } from '../components/Campaign/ImageController';
import { LiveUsersController } from '../components/Campaign/LiveUsersController';
import { MessageController } from '../components/Campaign/MessageController';
import { RtvUiController } from '../components/Campaign/RtvUiController';
import { TelemetryController } from '../components/Campaign/TelemetryController';
import CampaignDashboardLayout from '../components/layout/CampaignDashboardLayout';
import { CampaignService } from '../services/CampaignService';
import campaignStore from '../stores/CampaignStore';
import organizationStore from '../stores/OrganizationStore';

const Campaign: React.FC = observer(() => {
  const { id } = useParams<{ id: string }>();
  const { selectedOrg } = organizationStore;
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [droneUuid, setDroneUuid] = useState<string>('');

  const campaign = campaignStore.campaigns.find((c) => c.id === id);

  useEffect(() => {
    if (!id) {
      toast.error(t('campaign_view.errors.missing_campaign_id'));
      navigate('/campaigns', { replace: true });
      return () => {};
    }

    window.history.pushState({ inCampaign: true }, '');

    const onBack = async (e: PopStateEvent) => {
      console.log('Back button pressed, removing user from campaign');

      try {
        const isRemoved = await CampaignService.removeUsersFromCampaign(id);
        if (isRemoved) {
          toast.success(t('users_dropdown.left_campaign'));
        } else {
          toast.error(t('users_dropdown.left_failed'));
        }
      } catch (error) {
        console.error('Failed to remove user from campaign:', error);
        toast.error(t('users_dropdown.left_failed'));
      }

      navigate('/campaigns', { replace: true });
    };

    window.addEventListener('popstate', onBack);

    return () => {
      window.removeEventListener('popstate', onBack);
    };
  }, [id, navigate]);

  useEffect(() => {
    if (!campaign && selectedOrg) {
      campaignStore.fetchCampaigns(selectedOrg, t).then(() => {
        const found = campaignStore.campaigns.find((c) => c.id === id);
        if (!found) {
          toast.error(t('campaign_view.errors.campaign_not_found'));
          navigate('/campaigns');
        }
      });
    }
  }, [campaign, selectedOrg, id, t, navigate]);

  useEffect(() => {
    // Prefer devices (device_uuid strings) over legacy drones
    const deviceUuid = campaign?.devices?.[0];
    if (deviceUuid) {
      setDroneUuid(deviceUuid);
    }
  }, [campaign, t]);

  if (!selectedOrg || !campaign) {
    toast.error(t('selected_organization_or_campaign_not_found'));
    return null;
  }

  return (
    <CampaignDashboardLayout>
      <TelemetryController campaignId={campaign.id} orgId={selectedOrg} droneUuid={droneUuid} />
      <LiveUsersController campaignId={campaign.id} orgId={selectedOrg} />
      <ImageController campaignId={campaign.id} orgId={selectedOrg} droneUuid={droneUuid} />
      <RtvUiController campaignId={campaign.id} orgId={selectedOrg} droneUuid={droneUuid} />
      <MessageController campaignId={campaign.id} orgId={selectedOrg} />
      <CommandController campaignId={campaign.id} orgId={selectedOrg} droneUuid={droneUuid} />
      <CampaignView projectName='Campaigns' campaign={campaign} />
      {/** TODO replace Campaigns with Project Name */}
    </CampaignDashboardLayout>
  );
});

export default Campaign;
